import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  HouseSimple, Buildings, Users, Circuitry, Plus, Spinner, ArrowLeft,
  Receipt, PencilSimple, Trash, Eye, UserMinus, Clock,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  occupied: { label: "Dolu", className: "bg-emerald-100 text-emerald-700" },
  vacant: { label: "Bos", className: "bg-amber-100 text-amber-700" },
  maintenance: { label: "Bakim", className: "bg-slate-100 text-slate-600" },
};

export default function PropertyDetailPage() {
  const { propertyId } = useParams();
  const { user } = useAuth();
  const [property, setProperty] = useState(null);
  const [apartments, setApartments] = useState([]);
  const [units, setUnits] = useState([]);
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("units");

  // Fee Dialog
  const [feeDialogOpen, setFeeDialogOpen] = useState(false);
  const [feeForm, setFeeForm] = useState({ name: "", amount: 0, billing_day: 1, unit_id: "", apartment_id: "" });
  const [editFee, setEditFee] = useState(null);
  const [deleteFeeTarget, setDeleteFeeTarget] = useState(null);

  // Occupancy dialog
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyUnit, setHistoryUnit] = useState(null);
  const [occupancyHistory, setOccupancyHistory] = useState([]);

  // Checkout dialog
  const [checkoutTarget, setCheckoutTarget] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [propRes, aptsRes, unitsRes, feesRes] = await Promise.all([
        axios.get(`${API}/api/properties/${propertyId}`),
        axios.get(`${API}/api/properties/${propertyId}/apartments`),
        axios.get(`${API}/api/properties/${propertyId}/units`),
        axios.get(`${API}/api/unit-fixed-fees?property_id=${propertyId}`),
      ]);
      setProperty(propRes.data);
      setApartments(aptsRes.data);
      setUnits(unitsRes.data);
      setFees(feesRes.data);
    } catch {
      toast.error("Bina verileri yuklenemedi");
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const loadHistory = async (unit) => {
    setHistoryUnit(unit);
    try {
      const res = await axios.get(`${API}/api/units/${unit.id}/occupancy-history`);
      setOccupancyHistory(res.data);
      setHistoryDialogOpen(true);
    } catch {
      toast.error("Gecmis yuklenemedi");
    }
  };

  const handleCheckout = async () => {
    if (!checkoutTarget) return;
    try {
      const res = await axios.post(
        `${API}/api/units/${checkoutTarget.id}/checkout-tenant?reason=kiracı_degisimi`,
        {}
      );
      toast.success(`${res.data.tenant_name} cikarildi (bakiye: ${res.data.balance_at_checkout?.toFixed(2)} TL)`);
      setCheckoutTarget(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Islem basarisiz");
    }
  };

  // Fee CRUD
  const handleFeeSubmit = async () => {
    if (!feeForm.name.trim()) { toast.error("Gider adi zorunlu"); return; }
    if (feeForm.amount <= 0) { toast.error("Tutar 0'dan buyuk olmali"); return; }
    try {
      if (editFee) {
        await axios.put(`${API}/api/unit-fixed-fees/${editFee.id}`,
          { name: feeForm.name, amount: feeForm.amount, billing_day: feeForm.billing_day }
          );
        toast.success("Gider guncellendi");
      } else {
        const payload = {
          property_id: propertyId,
          name: feeForm.name,
          amount: feeForm.amount,
          billing_day: feeForm.billing_day,
        };
        if (feeForm.unit_id) payload.unit_id = feeForm.unit_id;
        else if (feeForm.apartment_id) payload.apartment_id = feeForm.apartment_id;
        await axios.post(`${API}/api/unit-fixed-fees`, payload);
        toast.success("Gider eklendi");
      }
      setFeeDialogOpen(false);
      setEditFee(null);
      setFeeForm({ name: "", amount: 0, billing_day: 1, unit_id: "", apartment_id: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Islem basarisiz");
    }
  };

  const handleDeleteFee = async () => {
    if (!deleteFeeTarget) return;
    try {
      await axios.delete(`${API}/api/unit-fixed-fees/${deleteFeeTarget.id}`);
      toast.success("Gider silindi");
      setDeleteFeeTarget(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silinemedi");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size={32} className="animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!property) {
    return <div className="text-center py-20 text-slate-500">Bina bulunamadi</div>;
  }

  const totalFeeAmount = fees.filter(f => f.is_active).reduce((s, f) => s + f.amount, 0);

  return (
    <div className="space-y-6" data-testid="property-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/properties">
          <Button variant="ghost" size="icon"><ArrowLeft size={20} /></Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{property.name}</h1>
          <p className="text-sm text-slate-500">{property.address}{property.city ? `, ${property.city}` : ""}</p>
        </div>
        <Badge variant="secondary" className="ml-auto">{property.property_type}</Badge>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard icon={Buildings} label="Apartman" value={apartments.length} color="indigo" />
        <StatCard icon={HouseSimple} label="Toplam Unite" value={property.total_units} color="slate" />
        <StatCard icon={Users} label="Dolu" value={property.occupied_units} color="emerald" />
        <StatCard icon={HouseSimple} label="Bos" value={property.vacant_units} color="amber" />
        <StatCard icon={Receipt} label="Sabit Gider" value={`${totalFeeAmount.toFixed(0)} TL`} color="violet" />
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="units" data-testid="tab-units">Uniteler ({units.length})</TabsTrigger>
          <TabsTrigger value="fees" data-testid="tab-fees">Sabit Giderler ({fees.length})</TabsTrigger>
        </TabsList>

        {/* Units Tab */}
        <TabsContent value="units" className="mt-4">
          {units.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-slate-500">Bu binada unite yok.</CardContent></Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Unite</TableHead>
                      <TableHead>Apartman</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead>Kiraci</TableHead>
                      <TableHead>Cihaz</TableHead>
                      <TableHead>kWh</TableHead>
                      <TableHead className="text-right">Islemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {units.map((u) => {
                      const s = STATUS_BADGE[u.status] || STATUS_BADGE.vacant;
                      return (
                        <TableRow key={u.id} data-testid={`unit-row-${u.id}`}>
                          <TableCell className="font-medium">{u.name}</TableCell>
                          <TableCell className="text-slate-500">{u.apartment_name || "-"}</TableCell>
                          <TableCell>
                            <Badge className={s.className}>{s.label}</Badge>
                          </TableCell>
                          <TableCell>{u.tenant_name || <span className="text-slate-400">-</span>}</TableCell>
                          <TableCell>
                            {u.device_name ? (
                              <span className="flex items-center gap-1">
                                <span className={`w-2 h-2 rounded-full ${u.device_online ? "bg-emerald-500" : "bg-slate-300"}`} />
                                {u.device_name}
                              </span>
                            ) : <span className="text-slate-400">-</span>}
                          </TableCell>
                          <TableCell>{u.device_kwh_balance != null ? `${u.device_kwh_balance.toFixed(1)}` : "-"}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="icon" onClick={() => loadHistory(u)}
                                title="Kiraci Gecmisi" data-testid={`history-btn-${u.id}`}>
                                <Clock size={16} />
                              </Button>
                              {u.tenant_id && (
                                <Button variant="ghost" size="icon" onClick={() => setCheckoutTarget(u)}
                                  title="Kiraciyi Cikar" data-testid={`checkout-btn-${u.id}`}>
                                  <UserMinus size={16} className="text-red-500" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Fees Tab */}
        <TabsContent value="fees" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-slate-500">Bina geneli ve unite bazli sabit giderler</p>
            <Button size="sm" onClick={() => {
              setEditFee(null);
              setFeeForm({ name: "", amount: 0, billing_day: 1, unit_id: "", apartment_id: "" });
              setFeeDialogOpen(true);
            }} data-testid="add-fee-btn">
              <Plus size={16} className="mr-1" /> Gider Ekle
            </Button>
          </div>
          {fees.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-slate-500">Sabit gider tanimlanmamis.</CardContent></Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Gider</TableHead>
                      <TableHead>Tutar</TableHead>
                      <TableHead>Kapsam</TableHead>
                      <TableHead>Fatura Gunu</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead className="text-right">Islemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fees.map((f) => (
                      <TableRow key={f.id} data-testid={`fee-row-${f.id}`}>
                        <TableCell className="font-medium">{f.name}</TableCell>
                        <TableCell>{f.amount.toFixed(2)} TL</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {f.scope === "property" ? "Bina Geneli" : f.scope === "apartment" ? "Apartman" : "Unite"}
                          </Badge>
                        </TableCell>
                        <TableCell>Her ayin {f.billing_day}. gunu</TableCell>
                        <TableCell>
                          <Badge className={f.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}>
                            {f.is_active ? "Aktif" : "Pasif"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => {
                              setEditFee(f);
                              setFeeForm({ name: f.name, amount: f.amount, billing_day: f.billing_day, unit_id: "", apartment_id: "" });
                              setFeeDialogOpen(true);
                            }}><PencilSimple size={16} /></Button>
                            <Button variant="ghost" size="icon" onClick={() => setDeleteFeeTarget(f)}>
                              <Trash size={16} className="text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Occupancy History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="occupancy-history-dialog">
          <DialogHeader>
            <DialogTitle>Kiraci Gecmisi — {historyUnit?.name}</DialogTitle>
          </DialogHeader>
          {occupancyHistory.length === 0 ? (
            <p className="text-slate-500 py-4 text-center">Gecmis kaydi yok.</p>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kiraci</TableHead>
                    <TableHead>Giris</TableHead>
                    <TableHead>Cikis</TableHead>
                    <TableHead>Bakiye</TableHead>
                    <TableHead>Borc</TableHead>
                    <TableHead>Sebep</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {occupancyHistory.map((h) => (
                    <TableRow key={h.id}>
                      <TableCell className="font-medium">{h.tenant_name || "-"}</TableCell>
                      <TableCell className="text-sm">{h.move_in_date ? new Date(h.move_in_date).toLocaleDateString("tr") : "-"}</TableCell>
                      <TableCell className="text-sm">
                        {h.move_out_date ? new Date(h.move_out_date).toLocaleDateString("tr") : (
                          <Badge className="bg-emerald-100 text-emerald-700">Aktif</Badge>
                        )}
                      </TableCell>
                      <TableCell>{h.balance_at_checkout != null ? `${h.balance_at_checkout.toFixed(2)} TL` : "-"}</TableCell>
                      <TableCell>{h.debt_at_checkout != null ? `${h.debt_at_checkout.toFixed(2)} TL` : "-"}</TableCell>
                      <TableCell className="text-sm text-slate-500">{h.checkout_reason || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Checkout Confirm */}
      <AlertDialog open={!!checkoutTarget} onOpenChange={(o) => !o && setCheckoutTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kiraciyi Cikar</AlertDialogTitle>
            <AlertDialogDescription>
              "{checkoutTarget?.tenant_name}" kiracisini "{checkoutTarget?.name}" unitesinden cikarmak istediginizden emin misiniz?
              Mevcut bakiye ve borc bilgisi gecmise kaydedilecektir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Iptal</AlertDialogCancel>
            <AlertDialogAction onClick={handleCheckout} className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-checkout-btn">
              Kiraciyi Cikar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Fee Dialog */}
      <Dialog open={feeDialogOpen} onOpenChange={setFeeDialogOpen}>
        <DialogContent data-testid="fee-dialog">
          <DialogHeader>
            <DialogTitle>{editFee ? "Gider Duzenle" : "Yeni Sabit Gider"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Gider Adi *</Label>
              <Input value={feeForm.name} onChange={e => setFeeForm({...feeForm, name: e.target.value})}
                placeholder="Ornek: Aidat, Temizlik, Su" data-testid="fee-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tutar (TL) *</Label>
                <Input type="number" step="0.01" min="0" value={feeForm.amount}
                  onChange={e => setFeeForm({...feeForm, amount: parseFloat(e.target.value) || 0})}
                  data-testid="fee-amount-input" />
              </div>
              <div>
                <Label>Fatura Gunu (1-28)</Label>
                <Input type="number" min={1} max={28} value={feeForm.billing_day}
                  onChange={e => setFeeForm({...feeForm, billing_day: parseInt(e.target.value) || 1})}
                  data-testid="fee-billing-day-input" />
              </div>
            </div>
            {!editFee && (
              <div>
                <Label>Kapsam</Label>
                <Select value={feeForm.unit_id ? "unit" : feeForm.apartment_id ? "apartment" : "property"}
                  onValueChange={(v) => {
                    if (v === "property") setFeeForm({...feeForm, unit_id: "", apartment_id: ""});
                    else if (v === "apartment") setFeeForm({...feeForm, unit_id: ""});
                    else setFeeForm({...feeForm, apartment_id: ""});
                  }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="property">Bina Geneli</SelectItem>
                    <SelectItem value="apartment">Belirli Apartman</SelectItem>
                    <SelectItem value="unit">Belirli Unite</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFeeDialogOpen(false)}>Iptal</Button>
            <Button onClick={handleFeeSubmit} data-testid="fee-submit-btn">
              {editFee ? "Guncelle" : "Ekle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Fee Confirm */}
      <AlertDialog open={!!deleteFeeTarget} onOpenChange={(o) => !o && setDeleteFeeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gider Silinecek</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteFeeTarget?.name}" giderini silmek istediginizden emin misiniz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Iptal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteFee} className="bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    indigo: "bg-indigo-50 text-indigo-600",
    slate: "bg-slate-50 text-slate-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    violet: "bg-violet-50 text-violet-600",
  };
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color] || colors.slate}`}>
          <Icon size={20} weight="duotone" />
        </div>
        <div>
          <div className="text-lg font-semibold text-slate-900">{value}</div>
          <div className="text-xs text-slate-500">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}
